import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi } from "ag-grid-community";
import { useMemo, useRef, useState, useEffect } from "react";
import _ from "lodash";
import { Icon } from "@iconify/react";
import { Box, HStack, VStack } from "../../../components/utils";
import { SummaryRole } from "../../../types/modelling";
import { getSalaryRangeForUI } from "../modellingUtils";
import { useOrgTemplateStore } from "../../../store/useOrgTemplateStore";
import OrganiSationStructureDataProcessor, {
  OrgSummaryData,
} from "./utils/OrgStructureJSONDataProc";
import { OrgSummaryDoughnutChart } from "./utils/OrgSummaryDoughNutChart";
import {
  getCurrencySymbol,
  getCurrencyType,
} from "../../../utils/currencyFormatter";
import { RandomDepartmentColorGenerator } from "./utils/RandomDepartmentColorGenerator";

export interface SummaryChartData {
  labels: (string | number)[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string[];
    borderColor: string[];
  }[];
  centerText: string;
}

export default function OrgStructureSummary() {
  const currencyType = getCurrencyType();
  const currencySymbol = getCurrencySymbol();
  const gridApi = useRef<GridApi | any>(null);
  const onGridReady = (params: any) => {
    gridApi.current = params.api;
    params.api.sizeColumnsToFit();
  };
  const { selectedCompanyData } = useOrgTemplateStore();
  const [keyRoles, setKeyRoles] = useState<Array<SummaryRole>>([]);
  const [summaryData, setSummaryData] = useState<OrgSummaryData>();
  const [chartData, setChartData] = useState<SummaryChartData>();
  const rowData = useMemo(() => keyRoles || [], [keyRoles]);

  useEffect(() => {
    if (selectedCompanyData && selectedCompanyData.departments) {
      const data: SummaryRole[] = [];
      Object.entries(selectedCompanyData.departments).forEach(
        ([departmentName, depData]) => {
          Object.entries(depData.levels).forEach(([levelName, levData]) => {
            Object.entries(levData.roles).forEach(([roleName, roleData]) => {
              data.push({
                department: departmentName,
                level: levelName,
                role: { role: roleName, keyRole: roleData.keyRole },
                salaryRange: {
                  min: roleData.salaryRange.min,
                  max: roleData.salaryRange.max,
                },
              });
            });
          });
        }
      );
      const processedData = new OrganiSationStructureDataProcessor(
        undefined,
        selectedCompanyData
      );
      const sumData = processedData.getSummarySheetDetails();
      const departmentChartData = {
        labels: ["Key Roles", "General"],
        datasets: [
          {
            label: "# of Roles",
            data: [sumData.noOfKeyRoles, sumData.noOfGeneralRoles],
            backgroundColor: ["#9D6E48", "#50CD89"],
            borderColor: ["#9D6E48", "#50CD89"],
          },
        ],
        centerText: "No of Roles",
      };
      setChartData(departmentChartData);
      setSummaryData(sumData);
      setKeyRoles(data);
    }
  }, [selectedCompanyData]);

  function KeyRoleCellRenderer(params: any) {
    if (params.value.keyRole) {
      return (
        <HStack className="items-center gap-2">
          <div>{params.value.role}</div>
          <img
            src="../assets/KeyRole.svg"
            alt="llll"
            height={"20px"}
            width={"20px"}
          />
        </HStack>
      );
    } else {
      return <HStack>{params.value.role}</HStack>;
    }
  }

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      suppressMenu: true,
      cellClass: "multiline text-xs font-medium text-gray-dark leading-5",
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      cellStyle: {
        lineHeight: "1.5",
        display: "flex",
        alignItems: "center",
        justifyContent: "left",
      },
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
    }),
    []
  );
  let index = 0;
  let prevDept = "";
  const columnDefs: ColDef[] = [
    {
      headerName: "DEPARTMENT",
      autoHeight: true,
      field: "department",
      initialWidth: 150,
      minWidth: 150,
      width: 150,
      flex: 1,
      filter: "agSetColumnFilter",
      sortable: true,
      menuTabs: ["filterMenuTab"],
      cellClass: "removePadding",
      cellStyle: (params: any) => {
        const existingDept = params.value;
        if (existingDept === prevDept) index -= 1;
        const color = RandomDepartmentColorGenerator(index, existingDept);
        index = index > 12 ? 0 : index + 1;
        prevDept = existingDept;
        return {
          color: "#ffffff",
          backgroundColor: color,
          display: "flex",
          "align-items": "center",
          lineHeight: "1.5",
          "justify-content": "center",
        };
      },
    },
    {
      headerName: "LEVEL",
      autoHeight: true,
      field: "level",
      flex: 1,
      filter: "agSetColumnFilter",
      initialWidth: 100,
      minWidth: 100,
      sortable: true,
      menuTabs: ["filterMenuTab"],
    },
    {
      headerName: "ROLE",
      autoHeight: true,
      field: "role",
      flex: 1,
      filter: "agSetColumnFilter",
      initialWidth: 200,
      minWidth: 200,
      sortable: true,
      menuTabs: ["filterMenuTab"],
      cellRenderer: KeyRoleCellRenderer,
    },
    {
      headerName: "Salary Range",
      autoHeight: true,
      field: "salaryRange",
      filter: "agSetColumnFilter",
      initialWidth: 150,
      valueFormatter: (params) =>
        params.data
          ? getSalaryRangeForUI(
              params.data?.salaryRange,
              currencySymbol,
              currencyType
            )
          : getSalaryRangeForUI(params.value, currencySymbol, currencyType),
      flex: 1,
      minWidth: 150,
      sortable: true,
      menuTabs: ["filterMenuTab"],
    },
  ];

  return (
    <VStack className="w-full gap-6">
      <VStack className="justify-between gap-1 pb-3 pl-6 font-medium border-b-[0.5px] pt-7">
        <HStack className="flex justify-between font-semibold text-lg1 text-black-501 ">
          Summary
        </HStack>
        <HStack className="flex font-medium text-sm3 text-gray-401">
          Company-wide employment and pay scale structure.
        </HStack>
      </VStack>
      <VStack className="pb-6 pl-4 mt-4 shadow-lg ml-7 mr-7">
        <VStack className="w-full gap-16 ">
          <HStack className="justify-center w-full text-gray-500">
            <p className="text-sm font-medium">Overall Summary</p>
          </HStack>
          <HStack className="gap-10">
            <VStack className="flex-1 gap-6 pt-6 ">
              <HStack className="flex-1">
                <div className="flex-1 text-gray-500 text-sm2">
                  No. of Departments
                </div>
                <div className="flex-1 font-semibold text-lg2">
                  {summaryData?.noOfDepartments || 0}
                </div>
              </HStack>
              <HStack className="flex-1">
                <div className="flex-1 text-gray-500 text-sm2">
                  No. of Roles
                </div>
                <div className="flex-1 font-semibold text-lg2">
                  {(summaryData?.noOfGeneralRoles || 0) +
                    (summaryData?.noOfKeyRoles || 0)}
                </div>
              </HStack>
              <HStack className="flex-1">
                <div className="flex-1 text-gray-500 text-sm2">
                  Salary Range
                </div>
                <div className="flex-1 font-semibold text-lg2">
                  {getSalaryRangeForUI(
                    summaryData?.salaryRange || { min: 0, max: 0 },
                    currencySymbol,
                    currencyType
                  )}
                </div>
              </HStack>
            </VStack>
            <VStack className="flex-1 gap-6 ">
              <VStack>
                <OrgSummaryDoughnutChart
                  data={chartData}
                  total={
                    (summaryData?.noOfGeneralRoles || 0) +
                    (summaryData?.noOfKeyRoles || 0)
                  }
                />
              </VStack>
              <HStack className="items-center justify-center gap-3">
                <HStack className="items-center gap-1">
                  <Box className="w-4 h-3 bg-[#50CD89]"></Box>
                  <p> General</p>
                </HStack>
                <HStack className="items-center gap-1">
                  <Box className="w-4 h-3 bg-[#9D6E48]"></Box>
                  <p> Key Roles</p>
                </HStack>
              </HStack>
            </VStack>
          </HStack>
        </VStack>
      </VStack>
      <HStack className="justify-between w-full pt-6 pb-6 pl-8">
        <Box
          style={{
            height: `${
              rowData.length <= 3
                ? "300"
                : rowData.length >= 10
                ? "600"
                : rowData.length * 80
            }px`,
          }}
          className="w-full h-full max-h-full overflow-x-auto bg-black ag-theme-material"
        >
          <AgGridReact
            rowClass={
              "border-t border-dashed cursor-pointer hover:bg-slate-50 "
            }
            alwaysMultiSort
            onGridReady={onGridReady}
            animateRows={true}
            defaultColDef={defaultColDef}
            rowData={rowData}
            suppressCopyRowsToClipboard={true}
            suppressCopySingleCellRanges={true}
            suppressCellFocus={true}
            suppressMenuHide={true}
            columnDefs={columnDefs}
            suppressRowTransform={true}
            rowMultiSelectWithClick={true}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            overlayNoRowsTemplate={
              '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;   margin-top: 50px;">No Rows To Show</span>'
            }
          ></AgGridReact>
        </Box>
      </HStack>
    </VStack>
  );
}
